import { useEffect, useContext, useState, useMemo } from 'react';
import AppContext from '../../AppContext';
import { SectionData } from './SectionData';
import Environment from '../../Environment';
import LoadingText from '../Loading/LoadingText/LoadingText';
import { SubSectionData } from '../SubSection/SubSectionData';
import SubSection from '../SubSection/SubSection';
import { ReportView } from '../model/ReportView';
import { cachedFetch } from '../../utils/cachedFetch';

interface SectionProps {
  sectionKey: string;
  sectionTitle: string;
}

export default function Section({ sectionKey, sectionTitle }: SectionProps) {
  const {
    cocode,
    year,
    periodType,
    quarterlyViewMode,
    riskRepository,
    viewMode,
    yearsMode,
    beginRefresh,
    endRefresh
  } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sectionData, setSectionData] = useState<SectionData | null>(null);
  const [noRecordsMessage, setNoRecordsMessage] = useState<string | null>(null);

  // In published mode, riskRepository does not affect the URL or fetch decision —
  // filtering by risk repository is a display-time concern handled by SubSection.
  // Only track it as an effect dependency when in preview mode, so side-nav clicks
  // in published mode don't refetch every section.
  const fetchRelevantRiskRepository = useMemo(
    () => (viewMode === 'preview' ? riskRepository : null),
    [viewMode, riskRepository]
  );

  // Structural fingerprint — excludes yearsMode. Changes here tear down the
  // current data and show the LoadingText placeholder. yearsMode-only changes
  // take the soft-refresh path: existing sectionData stays on screen while the
  // background refetch runs and the ReportOptions toggle shows an inline
  // spinner via the global isRefreshing flag.
  const structuralKey = `${cocode}|${year}|${periodType}|${quarterlyViewMode}|${viewMode}|${sectionKey}|${fetchRelevantRiskRepository ?? ''}`;
  const [renderedStructuralKey, setRenderedStructuralKey] = useState(structuralKey);
  if (renderedStructuralKey !== structuralKey) {
    setRenderedStructuralKey(structuralKey);
    setIsLoading(true);
    setError(null);
    setSectionData(null);
    setNoRecordsMessage(null);
  }

  function isFullProfile() {
    return riskRepository?.toLowerCase() === ReportView.FULL_REPORT;
  }

  function handleResponse(data: any) {
    // Preview API wraps section data inside sectionResult
    const sectionPayload = data.sectionResult || data;
    if (sectionPayload.message && !sectionPayload.subSectionResults) {
      setNoRecordsMessage(sectionPayload.message);
      setSectionData(null);
    } else {
      setSectionData(sectionPayload);
    }
  }

  function buildBaseUrl() {
    let url = `${Environment.API_URL()}/section/${sectionKey}?cocode=${cocode}&year=${year}`;
    if (periodType) url += `&periodType=${periodType}`;
    if (quarterlyViewMode) url += `&viewMode=${quarterlyViewMode}`;
    if (yearsMode) url += `&yearsMode=${yearsMode}`;
    return url;
  }

  function buildPreviewUrl() {
    let url = `${Environment.API_URL()}/preview/section/${sectionKey}?cocode=${cocode}&year=${year}`;
    if (periodType) url += `&periodType=${periodType}`;
    if (quarterlyViewMode) url += `&viewMode=${quarterlyViewMode}`;
    if (yearsMode) url += `&yearsMode=${yearsMode}`;
    return url;
  }

  useEffect(() => {
    let cancelled = false;
    let ended = false;
    const end = () => {
      if (!ended) {
        ended = true;
        endRefresh?.();
      }
    };

    beginRefresh?.();
    const headers = { Authorization: `${sessionStorage.getItem('bearerToken')}` };
    const baseUrl = buildBaseUrl();
    // Quarterly draft data is only ever persisted as non-active (draft) metadata, so the
    // preview endpoint is the correct source. The published /section endpoint serves only
    // ACTIVE metadata — for quarterly CALCULATE rows it evaluates live and errors — so it
    // must not be used as the report-view membership pre-check for quarterly drafts.
    const isQuarterly = typeof periodType === 'string' && periodType.startsWith('QUARTER_');

    async function load() {
      try {
        if (viewMode === 'preview' && (isFullProfile() || isQuarterly)) {
          const res = await cachedFetch(buildPreviewUrl(), { headers });
          if (cancelled) return;
          if (!res.ok) {
            setError(new Error(res.statusText));
            return;
          }
          const data = await res.json();
          if (cancelled) return;
          handleResponse(data);
        } else if (viewMode === 'preview') {
          const res = await cachedFetch(baseUrl, { headers });
          if (cancelled) return;
          if (!res.ok) {
            setError(new Error(res.statusText));
            return;
          }
          const data = await res.json();
          if (cancelled) return;
          const belongsToView = data.sectionReportViews?.includes(riskRepository);
          if (belongsToView) {
            const previewRes = await cachedFetch(buildPreviewUrl(), { headers });
            if (cancelled) return;
            if (!previewRes.ok) {
              setError(new Error(previewRes.statusText));
              return;
            }
            const previewData = await previewRes.json();
            if (cancelled) return;
            handleResponse(previewData);
          } else {
            handleResponse(data);
          }
        } else {
          const res = await cachedFetch(baseUrl, { headers });
          if (cancelled) return;
          if (!res.ok) {
            setError(new Error(res.statusText));
            return;
          }
          const data = await res.json();
          if (cancelled) return;
          handleResponse(data);
        }
      } catch (err) {
        if (!cancelled) setError(err as Error);
      } finally {
        if (!cancelled) setIsLoading(false);
        end();
      }
    }
    load();

    return () => {
      cancelled = true;
      end();
    };
  }, [
    cocode,
    year,
    periodType,
    quarterlyViewMode,
    sectionKey,
    viewMode,
    yearsMode,
    fetchRelevantRiskRepository,
    beginRefresh,
    endRefresh
  ]);

  function shouldShowSectionTitle(currentReportView: ReportView) {
    return (
      currentReportView?.toLowerCase() === ReportView.FULL_REPORT ||
      sectionData?.sectionReportViews?.includes(currentReportView)
    );
  }

  // Only show the LoadingText placeholder for the initial load or a hard
  // structural change. Once we have sectionData, yearsMode refreshes update
  // the DOM in place; the toggle's inline spinner communicates the refetch.
  if (isLoading && !sectionData && !noRecordsMessage) {
    return <LoadingText label={sectionTitle} />;
  } else if (error) {
    return <p>Error retrieving section data for {sectionTitle}</p>;
  } else if (noRecordsMessage) {
    return (
      <div className="section" id={`section-${sectionKey}`}>
        <h2 data-testid={`section-${sectionKey}`} id={sectionKey} className="section_title">
          {sectionTitle}
        </h2>
        <p data-testid={`no-records-${sectionKey}`}>{noRecordsMessage}</p>
      </div>
    );
  } else if (sectionData) {
    return (
      <div className="section" id={`section-${sectionKey}`}>
        {shouldShowSectionTitle(riskRepository) && (
          <h2 data-testid={`section-${sectionKey}`} id={sectionKey} className="section_title">
            {sectionTitle}
          </h2>
        )}

        {sectionData.subSectionResults &&
          sectionData.subSectionResults.length > 0 &&
          sectionData.subSectionResults.map((subSection: SubSectionData) => {
            return (
              <SubSection
                subSectionData={subSection}
                sectionKey={sectionKey}
                key={subSection.subSectionKey}
              />
            );
          })}
      </div>
    );
  }
}
